import React from "react";

/**
 * THEMES catalogue
 *
 * Each entry can declare:
 *   - bg       : the CSS background applied to the spin-wheel stage
 *   - motif    : decorative overlay variant ("snow", "sparks", "diyas", "hearts", "balloons", "stripes", "glow")
 *   - preview  : url for the picker thumbnail (in /api/files/citydooh/themes/) — optional
 *   - tint     : dominant solid color used by the picker label glow
 *   - label    : human-friendly name shown in the wizard picker
 *
 * NEW premium themes (theme_01..theme_10) live under PREMIUM_THEMES.
 * Legacy themes (citydooh / christmas / diwali / newyear / valentine / birthday / sale / custom)
 * are preserved to remain backward-compatible with already-launched campaigns.
 */

const THEME_ASSET = (n) =>
  `/api/files/citydooh/themes/theme_${String(n).padStart(2, "0")}.png`;

// Premium themes (synced 1:1 with theme_01..theme_10 in object storage)
const PREMIUM_THEMES = {
  sky: {
    label: "Sky",
    tint: "#2E9DF7",
    motif: "glow",
    preview: THEME_ASSET(1),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(147,210,255,0.55), rgba(46,157,247,0.95) 55%, #0F4A8C 100%)",
  },
  blossom: {
    label: "Blossom",
    tint: "#EC4899",
    motif: "hearts",
    preview: THEME_ASSET(2),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(252,205,228,0.6), rgba(236,72,153,0.95) 55%, #831843 100%)",
  },
  onyx: {
    label: "Onyx",
    tint: "#111111",
    motif: "sparks",
    preview: THEME_ASSET(3),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(60,60,60,0.55), rgba(15,15,15,0.95) 55%, #000000 100%)",
  },
  sunset: {
    label: "Sunset",
    tint: "#F97316",
    motif: "glow",
    preview: THEME_ASSET(4),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(254,215,170,0.6), rgba(249,115,22,0.95) 55%, #7C2D12 100%)",
  },
  gold: {
    label: "Gold",
    tint: "#F5C518",
    motif: "sparks",
    preview: THEME_ASSET(5),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(253,224,71,0.7), rgba(245,197,24,0.95) 55%, #854D0E 100%)",
  },
  midnight: {
    label: "Midnight",
    tint: "#1E3A8A",
    motif: "sparks",
    preview: THEME_ASSET(6),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(59,130,246,0.45), rgba(30,58,138,0.95) 55%, #0B1B45 100%)",
  },
  royal: {
    label: "Royal",
    tint: "#7C3AED",
    motif: "glow",
    preview: THEME_ASSET(7),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(196,181,253,0.55), rgba(124,58,237,0.95) 55%, #3B0764 100%)",
  },
  emerald: {
    label: "Emerald",
    tint: "#16A34A",
    motif: "glow",
    preview: THEME_ASSET(8),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(134,239,172,0.55), rgba(22,163,74,0.95) 55%, #14532D 100%)",
  },
  coral: {
    label: "Coral",
    tint: "#F43F5E",
    motif: "hearts",
    preview: THEME_ASSET(9),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(252,165,165,0.6), rgba(244,63,94,0.95) 55%, #7F1D1D 100%)",
  },
  crimson: {
    label: "Crimson",
    tint: "#B91C1C",
    motif: "sparks",
    preview: THEME_ASSET(10),
    bg: "radial-gradient(closest-side at 50% 30%, rgba(239,68,68,0.55), rgba(185,28,28,0.95) 55%, #4C0519 100%)",
  },
};

// Legacy themes — preserved as-is for backward compatibility
const LEGACY_THEMES = {
  citydooh: {
    label: "CityDooh",
    tint: "#F5A800",
    motif: "sparks",
    bg: "radial-gradient(closest-side at 50% 20%, rgba(245,168,0,0.18), transparent 60%), linear-gradient(180deg, #000000 0%, #0a0a0a 100%)",
  },
  christmas: {
    label: "Christmas",
    tint: "#22C55E",
    motif: "snow",
    bg: "radial-gradient(closest-side at 50% 20%, rgba(245,208,97,0.16), transparent 60%), linear-gradient(180deg, hsl(155 43% 8%) 0%, hsl(156 44% 12%) 100%)",
  },
  diwali: {
    label: "Diwali",
    tint: "#F59E0B",
    motif: "diyas",
    bg: "radial-gradient(closest-side at 50% 22%, rgba(253,230,138,0.22), transparent 62%), linear-gradient(180deg, hsl(270 52% 8%) 0%, hsl(270 50% 14%) 100%)",
  },
  newyear: {
    label: "New Year",
    tint: "#22D3EE",
    motif: "sparks",
    bg: "radial-gradient(closest-side at 50% 18%, rgba(245,208,97,0.18), transparent 60%), linear-gradient(180deg, hsl(230 45% 5%) 0%, hsl(230 40% 10%) 100%)",
  },
  valentine: {
    label: "Valentine",
    tint: "#E11D48",
    motif: "hearts",
    bg: "radial-gradient(closest-side at 50% 18%, rgba(225,29,72,0.12), transparent 60%), linear-gradient(180deg, hsl(350 100% 97%) 0%, hsl(0 0% 100%) 100%)",
  },
  birthday: {
    label: "Birthday",
    tint: "#0EA5E9",
    motif: "balloons",
    bg: "radial-gradient(closest-side at 50% 18%, rgba(14,165,233,0.14), transparent 60%), linear-gradient(180deg, hsl(200 100% 97%) 0%, hsl(0 0% 100%) 100%)",
  },
  sale: {
    label: "Sale",
    tint: "#DC2626",
    motif: "stripes",
    bg: "radial-gradient(closest-side at 50% 18%, rgba(220,38,38,0.12), transparent 60%), linear-gradient(180deg, hsl(33 100% 96%) 0%, hsl(0 0% 100%) 100%)",
  },
  custom: {
    label: "Custom",
    tint: "#0F6B73",
    motif: "",
    bg: "",
  },
};

const THEMES = { ...PREMIUM_THEMES, ...LEGACY_THEMES };

// Exposed list for pickers — premium first, custom last
export const THEME_ORDER = [
  "sky",
  "blossom",
  "onyx",
  "sunset",
  "gold",
  "midnight",
  "royal",
  "emerald",
  "coral",
  "crimson",
  "citydooh",
  "christmas",
  "diwali",
  "newyear",
  "valentine",
  "birthday",
  "sale",
  "custom",
];

export const PREMIUM_THEME_KEYS = Object.keys(PREMIUM_THEMES);

export const getThemeMeta = (key) => THEMES[key] || THEMES.christmas;

export const ThemeWrap = ({ theme = "christmas", customBg, children, className = "" }) => {
  const cfg = THEMES[theme] || THEMES.christmas;
  const style = {
    backgroundImage: customBg
      ? `linear-gradient(180deg, rgba(0,0,0,0.32), rgba(0,0,0,0.32)), url(${customBg})`
      : cfg.bg,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
  };
  return (
    <div data-theme={theme} className={`relative citydooh-stage ${className}`} style={style}>
      {cfg.motif && <DecorLayer motif={cfg.motif} />}
      <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" aria-hidden />
      <div className="relative z-10">{children}</div>
    </div>
  );
};

const DecorLayer = ({ motif }) => {
  if (motif === "snow")
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {Array.from({ length: 18 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/70 blur-[1px]"
            style={{
              width: 6 + ((i * 7) % 6),
              height: 6 + ((i * 7) % 6),
              top: `${(i * 53) % 100}%`,
              left: `${(i * 37) % 100}%`,
              opacity: 0.7,
            }}
          />
        ))}
      </div>
    );
  if (motif === "hearts")
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {Array.from({ length: 10 }).map((_, i) => (
          <svg
            key={i}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            className="absolute text-white/25"
            style={{ top: `${(i * 53) % 100}%`, left: `${(i * 71) % 100}%` }}
          >
            <path fill="currentColor" d="M12 21s-7-4.35-7-10a4 4 0 017-2.65A4 4 0 0119 11c0 5.65-7 10-7 10z" />
          </svg>
        ))}
      </div>
    );
  if (motif === "diyas" || motif === "sparks")
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {Array.from({ length: 14 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 6,
              height: 6,
              top: `${(i * 53) % 100}%`,
              left: `${(i * 41) % 100}%`,
              background: motif === "diyas" ? "#FDE68A" : "rgba(255,255,255,0.85)",
              boxShadow:
                motif === "diyas"
                  ? "0 0 16px #FDE68A"
                  : "0 0 12px rgba(255,255,255,0.65)",
              opacity: 0.6,
            }}
          />
        ))}
      </div>
    );
  if (motif === "balloons")
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        {["#0EA5E9", "#F97316", "#22C55E", "#A78BFA"].map((c, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: 36,
              height: 44,
              left: `${10 + i * 24}%`,
              top: `${5 + (i % 2) * 6}%`,
              background: c,
              opacity: 0.3,
              filter: "blur(0.5px)",
            }}
          />
        ))}
      </div>
    );
  if (motif === "stripes")
    return (
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background: "repeating-linear-gradient(135deg, rgba(220,38,38,0.05) 0 18px, transparent 18px 40px)",
        }}
      />
    );
  if (motif === "glow")
    return (
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            "radial-gradient(closest-side at 50% 100%, rgba(255,255,255,0.18), transparent 70%)",
        }}
      />
    );
  return null;
};

export const themeConfettiColors = (theme) => {
  switch (theme) {
    // Premium
    case "sky": return ["#2E9DF7", "#93D2FF", "#FFFFFF"];
    case "blossom": return ["#EC4899", "#F9A8D4", "#FFFFFF", "#FACC15"];
    case "onyx": return ["#FFFFFF", "#A0A0A0", "#F5A800"];
    case "sunset": return ["#F97316", "#FED7AA", "#FACC15", "#FFFFFF"];
    case "gold": return ["#F5C518", "#FDE68A", "#FFFFFF", "#7C2D12"];
    case "midnight": return ["#3B82F6", "#93D2FF", "#F5D061", "#FFFFFF"];
    case "royal": return ["#7C3AED", "#C4B5FD", "#FACC15", "#FFFFFF"];
    case "emerald": return ["#16A34A", "#86EFAC", "#F5D061", "#FFFFFF"];
    case "coral": return ["#F43F5E", "#FCA5A5", "#FACC15", "#FFFFFF"];
    case "crimson": return ["#B91C1C", "#EF4444", "#F5D061", "#FFFFFF"];
    // Legacy
    case "citydooh": return ["#F5A800", "#FFFFFF", "#A0A0A0", "#000000"];
    case "christmas": return ["#E11D48", "#22C55E", "#F5D061", "#ffffff"];
    case "diwali": return ["#F59E0B", "#F97316", "#FDE68A", "#ffffff"];
    case "newyear": return ["#F5D061", "#22D3EE", "#ffffff"];
    case "valentine": return ["#E11D48", "#FB7185", "#FBCFE8"];
    case "birthday": return ["#0EA5E9", "#F97316", "#22C55E", "#A78BFA"];
    case "sale": return ["#DC2626", "#F59E0B", "#111827"];
    default: return ["#E11D48", "#22C55E", "#F5D061"];
  }
};

export default ThemeWrap;
